/**
 * La metadata de cada modelo, guardada en el navegador entre sesiones.
 *
 * Vive en un módulo propio y no en `components/shell/queries.ts` porque la usan
 * los dos lados: las consultas (para leer y guardar) y el store de sesión (para
 * borrarla al salir), y el store no puede importar las consultas sin armar un
 * ciclo.
 */

import { api } from '@/lib/api';
import type { ModelSpec } from '@/lib/types';

/**
 * POR QUÉ, medido en una copia local con la demora real de producción: cada
 * pantalla que se abría por primera vez en la sesión esperaba la metadata de su
 * modelo —30 a 150 KB, cinco consultas, ~1 s— y RECIÉN DESPUÉS pedía sus datos.
 * La lista de ventas tardaba 2,2 s y la mitad era eso. Y la metadata sin id
 * sólo cambia con un despliegue o al mover permisos.
 *
 * QUÉ SE GUARDA: sólo la variante SIN id (campos, vistas, permisos), por
 * usuario, por empresa y por versión del sistema. Nunca datos de negocio.
 *
 * CÓMO SE USA: React Query la toma como dato inicial con su fecha. Si tiene
 * menos de diez minutos (el mismo `staleTime` de siempre) se usa tal cual; si
 * es más vieja, la pantalla se dibuja al instante con ella y se refresca de
 * fondo. Los permisos los sigue decidiendo el servidor en cada pedido: lo
 * guardado sólo decide qué se dibuja mientras llega lo nuevo.
 *
 * VERSIÓN: la URL del propio módulo, que en el build lleva el hash del
 * archivo. Un despliegue nuevo cambia la URL y lo guardado por la versión
 * anterior deja de usarse (y se borra la próxima vez que se guarda algo).
 */
const VERSION_DE_LA_METADATA = (() => {
  try {
    return new URL(import.meta.url).pathname;
  } catch {
    return 'dev';
  }
})();
const PREFIJO_METADATA = 'nucleo:meta:';

/** Lo guardado y cuándo. `spec` es la metadata, o el árbol de menús. */
export interface MetaGuardada<T = ModelSpec> {
  t: number;
  spec: T;
}

/** Lo ya leído en esta pestaña, para no parsear el JSON en cada pantalla. */
const metaLeida = new Map<string, MetaGuardada<unknown> | null>();

export function claveGuardada(companyId: number | null, modelName: string): string | null {
  // Defensivo: un doble de `api` en un test puede no traerla.
  const usuario = typeof api.usuarioDelToken === 'function' ? api.usuarioDelToken() : null;
  if (usuario === null || modelName === '') return null;
  return `${PREFIJO_METADATA}${VERSION_DE_LA_METADATA}|${usuario}|${companyId ?? '-'}|${modelName}`;
}

export function leerMetaGuardada<T = ModelSpec>(clave: string | null): MetaGuardada<T> | undefined {
  if (clave === null) return undefined;
  if (metaLeida.has(clave)) return (metaLeida.get(clave) as MetaGuardada<T> | null) ?? undefined;
  let valor: MetaGuardada<T> | null = null;
  try {
    const crudo = localStorage.getItem(clave);
    if (crudo !== null) {
      const datos = JSON.parse(crudo) as Partial<MetaGuardada<T>>;
      if (typeof datos.t === 'number' && typeof datos.spec === 'object' && datos.spec !== null) {
        valor = { t: datos.t, spec: datos.spec };
      }
    }
  } catch {
    // Storage bloqueado o JSON roto: se pide como siempre.
  }
  metaLeida.set(clave, valor);
  return valor ?? undefined;
}

/** Borra la metadata guardada: toda, o la de otras versiones del sistema. */
export function olvidarMetaGuardada(soloOtrasVersiones = false): void {
  metaLeida.clear();
  try {
    const actual = `${PREFIJO_METADATA}${VERSION_DE_LA_METADATA}|`;
    for (let i = localStorage.length - 1; i >= 0; i -= 1) {
      const clave = localStorage.key(i);
      if (clave === null || !clave.startsWith(PREFIJO_METADATA)) continue;
      if (soloOtrasVersiones && clave.startsWith(actual)) continue;
      localStorage.removeItem(clave);
    }
  } catch {
    // Nada que hacer: sin storage no hay nada guardado.
  }
}

let versionesViejasBorradas = false;

export function guardarMeta<T = ModelSpec>(clave: string | null, spec: T): void {
  if (clave === null) return;
  const valor: MetaGuardada<T> = { t: Date.now(), spec };
  metaLeida.set(clave, valor);
  if (!versionesViejasBorradas) {
    versionesViejasBorradas = true;
    olvidarMetaGuardada(true);
  }
  try {
    localStorage.setItem(clave, JSON.stringify(valor));
  } catch {
    // Lleno: se hace lugar borrando la metadata guardada y se intenta una vez
    // más. Si tampoco entra, la pantalla sigue andando sin guardarla.
    olvidarMetaGuardada();
    try {
      localStorage.setItem(clave, JSON.stringify(valor));
      metaLeida.set(clave, valor);
    } catch {
      metaLeida.delete(clave);
    }
  }
}
